import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { PushPin as PinIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../utils/config';
import { formatServerDateLong } from '../utils/dateUtils';
import { responseErrorMessage } from '../utils/apiErrors';

interface NewsItem {
  newsId: string;
  title: string;
  summary: string;
  content: string;
  publishedDate: string;
  isPinned: boolean;
}

const NewsReader: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/news/public?limit=50`);
      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja
      // (eddig egy angol `throw new Error('Failed to fetch news')` után a catch FIX szövege ment ki, az indoklás — pl. a
      // sebességkorlát 429-es magyar mondata — elveszett). Nyilvános, token nélküli hívás: munkamenet-zár nincs, ezért a
      // sima `responseErrorMessage`. A catch-ág (csak hálózati bukás / hibás JSON) szándékosan fix magyar szöveg marad.
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setError(responseErrorMessage(body, response.status, 'Nem sikerült betölteni a híreket'));
        return;
      }
      const data = await response.json();
      setNews(data);
    } catch (err) {
      setError('Nem sikerült betölteni a híreket');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (news.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Jelenleg nincsenek hírek.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Hírek
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {news.map((item) => (
          <Card key={item.newsId} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {item.isPinned && (
                  <Chip icon={<PinIcon />} label="Kitűzött" size="small" color="primary" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {/* [Web UX 1. csomag (4.3), 2026-10-03] UTC, „Z” nélkül → 00:00–02:00 között az előző NAPOT mutatta. */}
                  {formatServerDateLong(item.publishedDate)}
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                {item.title}
              </Typography>
              {item.summary && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {item.summary}
                </Typography>
              )}
              {/* A tartalom sima szöveg (a szerkesztő multiline TextField); sortöréseket
                  a pre-line tartja meg. NEM HTML — így nincs XSS-kockázat. */}
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {item.content}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default NewsReader;
